import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { api, trackView } from "@/lib/api";
import { formatPostDate } from "@/lib/date";

const PostDialog = ({ postId, open, onOpenChange, apiClient = api, basePath = "/posts" }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !postId) return;
    let cancelled = false;
    setLoading(true);
    setPost(null);
    setError(null);
    (async () => {
      try {
        const { data } = await apiClient.get(`${basePath}/${postId}`);
        if (!cancelled) {
          setPost(data);
          // Track post view (only public, non-reserved scope)
          if (basePath === "/posts") trackView(`/post/${postId}`, postId);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Post non trovato");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [postId, open, apiClient, basePath]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-[#1a1918]/75 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="post-dialog"
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[94vw] max-w-3xl max-h-[90vh] overflow-y-auto bg-[#f9f8f6] border border-[#e5e1d8] shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Close
            data-testid="post-dialog-close"
            className="absolute right-3 top-3 z-10 p-2 bg-[#f9f8f6]/80 hover:bg-[#1a1918] hover:text-[#f9f8f6] transition-colors"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Chiudi</span>
          </DialogPrimitive.Close>

          {loading ? (
            <div className="p-10 font-sans text-sm text-[#4a4843]">caricamento…</div>
          ) : error ? (
            <div className="p-10 text-center">
              <DialogTitle className="font-serif text-2xl text-[#1a1918]">{error}</DialogTitle>
            </div>
          ) : post ? (
            <article className="px-5 sm:px-10 py-8 sm:py-10">
              <p className="font-sans text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#4a4843] mb-3">
                {formatPostDate(post.created_at)}
              </p>
              <DialogTitle asChild>
                <h2 data-testid="dialog-post-title" className="font-serif text-2xl sm:text-4xl tracking-tight leading-[1.1] font-light text-[#1a1918]">
                  {post.title}
                </h2>
              </DialogTitle>
              {post.author && (
                <p
                  data-testid="dialog-post-author"
                  className="font-serif italic text-base sm:text-lg text-[#4a4843] mt-2"
                >
                  di {post.author}
                </p>
              )}
              <div className="w-10 h-px bg-[#d4b886] my-6 sm:my-8" />

              {!post.cover_image_path && post.youtube_id && (
                <div className="mb-6 sm:mb-8 mx-auto max-w-[500px]">
                  <div className="aspect-video w-full">
                    <iframe
                      data-testid="dialog-youtube"
                      src={`https://www.youtube.com/embed/${post.youtube_id}`}
                      title={post.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                </div>
              )}

              <div
                data-testid="dialog-post-content"
                className="prose-dialog"
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                dangerouslySetInnerHTML={{ __html: post.content_html || "<p><em>Nessun contenuto.</em></p>" }}
              />
            </article>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default PostDialog;
